import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ListOrderHistory.css";
import { useAuth } from "../../utils/AuthContext";
import { url } from "../../Constant/TextConstant";
import Header from "../Header";
import Footer from "../Footer/Footer";
import { Helmet } from "react-helmet";
import { useUserLocation } from "../../utils/useUserLocation";
import { apiFetch } from "../../utils/apiClient.js";

const ListOrderHistory = () => {
  const [listOrders, setListOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
   // Replace the existing location state with the useUserLocation hook
   const {
     location: currentLocation,
     error: locationError,
     isLoading: isLocationLoading,
     updateLocation,
   } = useUserLocation();
 
   const [showModal, setShowModal] = useState(false);
   const [selectedCategory, setSelectedCategory] = useState(() => {
     return localStorage.getItem("selectedCategory") || "products";
   });
 
    useEffect(() => {
       window.scrollTo(0, 0);
     }, []);
   // Update localStorage whenever selectedCategory changes
   useEffect(() => {
     localStorage.setItem("selectedCategory", selectedCategory);
   }, [selectedCategory]);
 
   // Handler for modal close with location update
   const handleModalClose = (shouldClose, newCoordinates = null) => {
     setShowModal(false);
     if (newCoordinates) {
       // Update location through the hook's update function
       updateLocation({
         coordinates: {
           latitude: newCoordinates.latitude,
           longitude: newCoordinates.longitude,
         },
         address: newCoordinates.address || "",
         details: newCoordinates.details || null,
       });
     }
   };

  // Generate structured data for the orders
  const generateOrdersStructuredData = () => {
    if (!listOrders || listOrders.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Bulk Purchase History",
      description: "List of your bulk purchase orders",
      url: window.location.href,
      numberOfItems: listOrders.length,
      itemListOrder: "http://schema.org/ItemListOrderDescending",
      itemListElement: listOrders.map((order, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Order",
          orderNumber: order.orderNumber || "",
          orderDate: order.date ? new Date(order.date).toISOString() : "",
          orderStatus: order.deliveryStatus || "",
          customer: {
            "@type": "Person",
            name: order.name || "",
            email: order.email || "",
            telephone: order.customerMobile || "",
          },
        },
      })),
    };
  };

  const structuredData = generateOrdersStructuredData();

  useEffect(() => {
    const fetchListOrders = async () => {
      try {
        const response = await apiFetch(
          `${url.baseAPIUrl}/api/ListOrder/GetCustomerOrder?CustomerMobile=${currentUser}`
        );
        if (!response.ok) {
          throw new Error("Failed to load list orders");
        }
        const data = await response.json();
        setListOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchListOrders();
  }, [currentUser]);

  const getStatusColor = (status) => {
    if (!status) return "#9E9E9E";
    switch (status.toLowerCase()) {
      case "accepted":
        return "#4CAF50";
      case "processing":
        return "#FF9800";
      case "rejected":
        return "#F44336";
      default:
        return "#9E9E9E";
    }
  };

  const formatTime = (inputTime) => {
    if (!inputTime) return "";
    try {
      const [hours, minutes] = inputTime.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return inputTime;
    }
  };

  const handleBackToProducts = () => navigate("/");

  if (loading) {
    return (
      <>
        {" "}
        <Helmet>
          <title>Loading Order History | MyStore</title>
          <meta
            name="description"
            content="Loading your bulk purchase history"
          />
        </Helmet>
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Error Loading Orders | MyStore</title>
          <meta
            name="description"
            content="Error occurred while loading your order history"
          />
        </Helmet>
        <div className="error-container">
          <div className="error-icon">!</div>
          <h2>Error Loading Orders</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Bulk Purchase History (${listOrders.length}) | MyStore`}</title>
        <meta
          name="description"
          content={`View your bulk purchase history with ${listOrders.length} orders`}
        />
        <meta
          name="keywords"
          content="bulk orders, purchase history, order tracking, MyStore"
        />
        <meta
          property="og:title"
          content={`Bulk Purchase History (${listOrders.length}) | MyStore`}
        />
        <meta
          property="og:description"
          content={`View your bulk purchase history with ${listOrders.length} orders`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${window.location.origin}/bulk-orders-og-image.jpg`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`Bulk Purchase History (${listOrders.length}) | MyStore`}
        />
        <meta
          name="twitter:description"
          content={`View your bulk purchase history with ${listOrders.length} orders`}
        />
        <meta
          name="twitter:image"
          content={`${window.location.origin}/bulk-orders-twitter-image.jpg`}
        />
        <link rel="canonical" href={window.location.href} />
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>
      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />

      <div className="bulk-order-history">
        {listOrders.length === 0 ? (
          <div className="empty-orders-container">
            <div className="empty-orders-icon">
              <svg viewBox="0 0 24 24">
                <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z" />
              </svg>
            </div>
            <h2>No Orders Available</h2>
            <p>It looks like there are no orders to display at the moment.</p>
            <button onClick={handleBackToProducts} className="shop-now-button">
              Browse Products
            </button>
          </div>
        ) : (
          <>
            <header className="order-history-header">
              <h1>Bulk Purchase History</h1>
            </header>

            <main className="orders-container">
              {listOrders.map((order) => (
                <OrderCard
                  key={order.orderNumber || Math.random()}
                  order={order}
                  getStatusColor={getStatusColor}
                  formatTime={formatTime}
                />
              ))}
            </main>
          </>
        )}
      </div>

      <Footer />
    </>
  );
};

const OrderCard = ({ order, getStatusColor, formatTime }) => {
  const [expanded, setExpanded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);

  const toggleExpand = () => setExpanded(!expanded);
  const handleImageClick = () => setShowFullImage(true);
  const closeFullImage = (e) => {
    e.stopPropagation();
    setShowFullImage(false);
  };

  return (
    <>
      <div className={`order-card ${expanded ? "expanded" : ""}`}>
        <div className="order-summary" onClick={toggleExpand}>
          <div className="order-number">
            <span>Order #:</span>
            <strong>{order.OrderNumber || "N/A"}</strong>
          </div>
          <div className="order-date">
            <span>
              {order.Date ? new Date(order.Date).toLocaleDateString() : "N/A"}
            </span>
            <span>{formatTime(order.Time)}</span>
          </div>
          <div
            className="order-status"
            style={{ backgroundColor: getStatusColor(order.DeliveryStatus) }}
          >
            {order.DeliveryStatus || "Unknown"}
          </div>
          <div className="expand-icon">{expanded ? "−" : "+"}</div>
        </div>

        {expanded && (
          <div className="order-details">
            <div className="customer-info">
              <h3>Customer Details</h3>
              <p>
                <strong>Name:</strong> {order.Name || "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {order.Email || "N/A"}
              </p>
              <p>
                <strong>Mobile:</strong> {order.CustomerMobile || "N/A"}
              </p>
              <p>
                <strong>Address:</strong> {order.FullAddress || "N/A"}
              </p>
              <p>
                <strong>City:</strong> {order.City || "N/A"},{" "}
                {order.State || "N/A"}, {order.Pincode || "N/A"}
              </p>
            </div>

            {order.Image && (
              <div className="order-image-container">
                <h3>Product Image</h3>
                <div
                  className={`order-image ${imageLoaded ? "loaded" : ""}`}
                  onClick={handleImageClick}
                >
                  <img
                    src={`${url.productImageStartingPath}/${order.Image}`}
                    alt="Order product"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(false)}
                  />
                  {!imageLoaded && <div className="image-placeholder"></div>}
                </div>
              </div>
            )}

            <div className="order-description">
              <h3>Description</h3>
              <p>{order.Description || "No description available"}</p>
            </div>
          </div>
        )}
      </div>

      {showFullImage && order.image && (
        <div className="full-image-overlay" onClick={closeFullImage}>
          <div
            className="full-image-container"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${url.baseAPIUrl}${order.image}`}
              alt="Full size product"
            />
            <button className="close-button" onClick={closeFullImage}>
              &times;
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ListOrderHistory;
